import { frontDoorLinks } from "./front-door-links";
import * as stylex from "@stylexjs/stylex";
import { CoworldAboutLine as AboutLine } from "@softmax/product-ux/coworld-header";
import {
  StorefrontLead,
  StorefrontRequirements,
  stripBlockPx,
} from "@softmax/product-ux/catalog";
import { coworldSite } from "../../play/state";
import { CoworldHeader, coworldStats } from "./coworld-header";
import { ReplayEmbed } from "../../watch/replay";
import type { PreviewContent } from "../content/preview-content.types";
import { stageChromeFor } from "../copy/preview-copy";
import {
  EditToggleButton,
  HeroCta,
  HeroAbout,
  LockedRegion,
  EditableStage,
} from "../../store-editor";

export type StorefrontProps = {
  content: PreviewContent;
  now?: string;
  isMember: boolean;
  inProgress?: boolean;
  doorHref: string;
  memberLabel?: string;
  splitHero?: boolean;
};
export function storefrontSlots({
  content,
  now = content.generatedAt,
  isMember,
  inProgress,
  doorHref,
  memberLabel,
  splitHero,
}: StorefrontProps) {
  const stats = coworldStats({
    preview: content,
    now,
    prize: content.rewards?.prize ?? null,
  });
  const site = coworldSite[content.slug.replace(/\/.*$/, "")];
  const replayItem = content.media.find((item) => item.kind === "replay");
  const lead = (
    <LockedRegion label="Set by Softmax">
      <StorefrontLead game={content.name} />
    </LockedRegion>
  );
  const cta = (
    <HeroCta
      doorHref={doorHref}
      previewSlug={content.route.game.slug}
      isMember={isMember}
      liveLabel={inProgress ? "Continue on /play" : content.copy.ctaLabel}
      memberLabel={memberLabel}
      large={splitHero}
    />
  );
  const requirements = (
    <LockedRegion as="span" label="Set by Softmax">
      <StorefrontRequirements
        isMember={isMember}
        links={frontDoorLinks(content.details.links)}
      />
    </LockedRegion>
  );
  const door = (
    <>
      {lead}
      <div data-store-play {...stylex.props(styles.door)}>
        {cta}
        {requirements}
      </div>
    </>
  );
  return {
    header: (
      <CoworldHeader
        slug={content.slug}
        coverSrc={content.art ?? undefined}
        icon={content.icon}
        name={content.name}
        site={site}
        about={
          <HeroAbout blurb={content.blurb} closingLine={content.closingLine}>
            {site === undefined && content.blurb === "" ? null : (
              <AboutLine
                site={site}
                hideSiteOnNarrow
                blurb={content.blurb}
                more={content.closingLine}
              />
            )}
          </HeroAbout>
        }
        stats={stats}
        anchored={splitHero}
        after={splitHero ? door : undefined}
      />
    ),
    lead,
    cta,
    requirements,
    editor: <EditToggleButton />,
    mediaStripHeight: content.media.length > 1 ? stripBlockPx : 0,
    media: (
      <EditableStage
        liveMedia={content.media}
        name={content.name}
        uppercase={content.heroUppercase}
        landscape={splitHero}
        replayEmbed={
          replayItem?.episodeId ? (
            <ReplayEmbed
              coworld={content.coworldName}
              episodeId={replayItem.episodeId}
              title={`Replay of ${replayItem.alt}`}
              chrome={stageChromeFor({
                copy: content.copy,
                coworldVersion: replayItem.coworldVersion ?? null,
              })}
              viewerSize={content.copy.replayViewerSize}
              poster={replayItem.poster}
            />
          ) : undefined
        }
      />
    ),
  };
}
const styles = stylex.create({
  door: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: { default: "stretch", "@media (min-width: 640px)": "start" },
  },
});
